import React, { useState } from "react";
import { MuscleGroupWeight, ExerciseReference } from "../../api/types.ts";
import { RoutineExerciseSets } from "../routines/RoutineExerciseSets.tsx";
import { MultiInlineEdit } from "../inputs/MultiInlineEdit.tsx";
import { Select } from "../inputs/Select.tsx";
import {
  ExerciseIcon,
  FetchedExercise,
  getExerciseName,
} from "../../util/exercise.tsx";

const SharedRoutineExerciseCard = ({
  exercise,
  index,
  focusedIndex,
  setFocusedIndex,
  editingExercise,
  setEditingExercise,
  availableExercises,
  onExerciseChange,
  isEditable,
}: {
  exercise: ExerciseReference;
  index: number;
  focusedIndex: number | null;
  setFocusedIndex: (index: number | null) => void;
  editingExercise: {
    index: number;
    exerciseId: string;
    resistanceUnit: "kg" | "lbs";
  } | null;
  setEditingExercise: (
    exercise: {
      index: number;
      exerciseId: string;
      resistanceUnit: "kg" | "lbs";
    } | null,
  ) => void;
  availableExercises: Map<string, FetchedExercise>;
  onExerciseChange: (
    routineExerciseIndex: number,
    newUnit: "kg" | "lbs",
    newExerciseId: string,
    newExerciseIndex?: number,
  ) => void;
  totalExercises: number;
  isEditable: boolean;
}) => {
  const handleSaveExerciseEdit = (index: number) => {
    if (!onExerciseChange || !setEditingExercise) {
      return;
    }

    if (editingExercise && editingExercise.index === index) {
      onExerciseChange(
        index,
        editingExercise.resistanceUnit,
        editingExercise.exerciseId,
      );
    }
    setEditingExercise(null);
  };

  const handleCancelExerciseEdit = (index: number) => {
    if (!setEditingExercise) {
      return;
    }

    setEditingExercise(null);
  };

  return (
    <div className="bg-gray-50 rounded-md p-3">
      <div className="flex justify-between items-start">
        <div className="w-full">
          <div
            className="flex flex-wrap items-center gap-2 w-full justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-2 flex-1">
                {focusedIndex !== index && (
                  <ExerciseIcon
                    exerciseId={exercise.exercise_id}
                    availableExercises={availableExercises}
                  />
                )}

                <MultiInlineEdit
                  displayValue={getExerciseName(
                    exercise.exercise_id,
                    availableExercises,
                  )}
                  className="flex flex-wrap gap-2"
                  displayClassName="text-gray-800 font-semibold"
                  onSave={() => handleSaveExerciseEdit(index)}
                  onCancel={() => handleCancelExerciseEdit(index)}
                  displayAs="h5"
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={() => setFocusedIndex(null)}
                  disabled={
                    !isEditable &&
                    !!availableExercises.get(exercise.exercise_id)
                  }
                >
                  <Select
                    className="max-w-36 sm:max-w-64"
                    value={
                      editingExercise?.index === index
                        ? editingExercise.exerciseId
                        : exercise.exercise_id
                    }
                    options={Array.from(availableExercises.entries()).map(
                      (entry) => ({
                        id: entry[0],
                        label: entry[1]?.exercise?.name ?? "Unknown Exercise",
                        style: entry[1]?.external
                          ? { fontStyle: "italic" }
                          : undefined,
                      }),
                    )}
                    onChange={(newId) => {
                      setEditingExercise({
                        index,
                        exerciseId: newId,
                        resistanceUnit:
                          editingExercise?.resistanceUnit ||
                          exercise.resistance_unit,
                      });
                    }}
                  />
                  {isEditable && (
                    <Select
                      className="max-w-18"
                      value={
                        editingExercise?.index === index
                          ? editingExercise.resistanceUnit
                          : exercise.resistance_unit
                      }
                      options={["kg", "lbs"]}
                      onChange={(newUnit) => {
                        setEditingExercise({
                          index,
                          exerciseId:
                            editingExercise?.exerciseId || exercise.exercise_id,
                          resistanceUnit: newUnit as "kg" | "lbs",
                        });
                      }}
                    />
                  )}
                </MultiInlineEdit>
              </div>
              {!availableExercises.get(exercise.exercise_id) && (
                <span className="text-xs italic text-danger p-1">
                  This exercise no longer exists. You will not be able to use or
                  edit this routine until it no longer references this exercise.
                </span>
              )}
            </div>
          </div>
          <RoutineExerciseSets
            exercise={
              availableExercises.get(exercise.exercise_id)?.exercise ?? {
                id: exercise.exercise_id,
                name: "Unknown Exercise",
                primary_muscle_groups: [] as MuscleGroupWeight[],
                secondary_muscle_groups: [] as MuscleGroupWeight[],
                default_resistance_unit: "kg",
                start_resistance: 0,
                efficiency_factor: 1,
              }
            }
            routineExercise={exercise}
            routineExerciseIndex={index}
            isShared
          />
        </div>
      </div>
    </div>
  );
};

interface SharedRoutineExerciseListProps {
  resourceId: string;
  routineExercises: ExerciseReference[];
  availableExercises: Map<string, FetchedExercise>;
  onExerciseChange: (
    routineExerciseIndex: number,
    newUnit: "kg" | "lbs",
    newExerciseId: string,
    newExerciseIndex?: number,
  ) => void;
  isEditable: boolean;
}

const SharedRoutineExerciseList: React.FC<SharedRoutineExerciseListProps> = ({
  resourceId,
  routineExercises,
  availableExercises,
  onExerciseChange,
  isEditable,
}) => {
  const [editingExercise, setEditingExercise] = useState<{
    index: number;
    exerciseId: string;
    resistanceUnit: "kg" | "lbs";
  } | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  return (
    <div key={resourceId} className="space-y-3">
      {routineExercises.map((ExerciseReference, index) => (
        <div key={`${resourceId}-${index}`}>
          <SharedRoutineExerciseCard
            exercise={ExerciseReference}
            index={index}
            focusedIndex={focusedIndex}
            setFocusedIndex={setFocusedIndex}
            editingExercise={editingExercise}
            setEditingExercise={setEditingExercise}
            availableExercises={availableExercises}
            onExerciseChange={onExerciseChange}
            totalExercises={routineExercises.length}
            isEditable={isEditable}
          />
        </div>
      ))}
    </div>
  );
};

export { SharedRoutineExerciseList };
